/**
 * Der Bestand, wie das Geraet ihn sieht (CLAUDE.md §3.3, KONZEPT.md §15.1).
 *
 * „Die Oberflaeche zeigt das Ergebnis **sofort aus dem lokalen Stand**, nicht
 * erst nach Serverantwort." Seit Stufe 3 lesen alle Seiten, die im Einsatz
 * gebraucht werden, **nur** hier: Suche, Scan, Artikel, Lagerort, Baum,
 * Etiketten. Es gibt keinen zweiten Lesepfad zum Server, der offline anders
 * aussaehe als online.
 *
 * Der Zustand eines Artikels ist das, was das Log ergibt, **plus** das, was
 * noch in der Warteschlange liegt -- gerechnet mit derselben reinen Funktion
 * wie auf dem Server. Wer offline eine Kiste als defekt bucht, sieht sie
 * sofort als defekt, und sieht dasselbe, was der Server spaeter rechnet.
 *
 * Aenderungen an der Datenbank kommen von selbst an: `liveQuery` von Dexie
 * rechnet neu, sobald eine der gelesenen Tabellen sich aendert -- nach einer
 * Buchung, nach dem Abgleich, in jedem Tab.
 */

import { liveQuery } from "dexie";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  STANDARD_STATUSARTEN,
  type Artikel,
  type BenutzerKurz,
  type Buchung,
  type Einsatzstelle,
  type Lagerort,
  type LagerortKnoten,
  type StatusArt,
  type Zustand,
} from "@thw/contracts";
import {
  sortiereBuchungen,
  statusRegeln,
  unbekanntBuchung,
  zustandBerechnen,
  type StatusRegeln,
} from "@thw/core-domain";
import { alsBuchung } from "../api/warteschlange.js";
import { db, metaLesen } from "./datenbank.js";
import { inhaltVorwegnehmen } from "./packstueck.js";

export interface Bestand {
  artikel: ReadonlyMap<string, Artikel>;
  /** Der Zustand je Artikel -- Log plus Warteschlange. */
  zustaende: ReadonlyMap<string, Zustand>;
  lagerorte: ReadonlyMap<string, Lagerort>;
  einsatzstellen: ReadonlyMap<string, Einsatzstelle>;
  benutzer: ReadonlyMap<string, BenutzerKurz>;
  /** Nach `reihenfolge`. Ohne lokale Kopie die Grundausstattung. */
  statusArten: readonly StatusArt[];
  basis_adresse: string | null;
  /** Artikel mit Buchungen, die noch nicht beim Server sind. */
  wartend: ReadonlySet<string>;
}

/**
 * Liest den ganzen lokalen Bestand.
 *
 * Einige tausend Artikel sind fuer IndexedDB und den Arbeitsspeicher eines
 * Handys keine Last. Dafuer rechnet danach jede Suche im Speicher, ohne
 * Wartezeit (KONZEPT.md §18: „Suche mit sofortigem Ergebnis").
 */
export async function bestandLaden(): Promise<Bestand> {
  const [artikel, zustaende, lagerorte, einsatzstellen, benutzer, statusArten, basis, wartende] =
    await Promise.all([
      db.artikel.toArray(),
      db.zustaende.toArray(),
      db.lagerorte.toArray(),
      db.einsatzstellen.toArray(),
      db.benutzer.toArray(),
      db.statusArten.toArray(),
      metaLesen("basis_adresse"),
      db.buchungen.where("zustand").equals("wartet").toArray(),
    ]);

  const arten =
    statusArten.length > 0
      ? [...statusArten].sort((a, b) => a.reihenfolge - b.reihenfolge)
      : STANDARD_STATUSARTEN;
  const artikelKarte = new Map(artikel.map((a) => [a.id, a]));
  const zustandKarte = new Map<string, Zustand>(zustaende.map((z) => [z.artikel_id, z]));
  const wartend = new Set(wartende.map((w) => w.buchung.artikel_id));

  // Nur die Artikel mit offenen Buchungen neu rechnen -- und den Inhalt
  // wartend gebuchter Packstuecke, der mitgeht (ADR 0012). Fuer alle anderen
  // gilt, was der Abgleich zuletzt aus dem Log gerechnet hat.
  if (wartend.size > 0) {
    const regeln = statusRegeln(arten);
    const offen = wartende.map(alsBuchung);
    // Verloren oder gestohlen: Die Verschiebung nach „Unbekannt“ leitet der
    // Server ab -- hier wird sie vorweggenommen (ADR 0020). Eine lokale Kopie
    // von vor Vertrag 2.9 kennt den Ort nicht; dann eben erst nach dem Abgleich.
    const unbekannt = lagerorte.find((l) => (l.unbekannt as boolean | undefined) === true);
    if (unbekannt !== undefined) {
      for (const b of [...offen]) {
        const weg = unbekanntBuchung(
          b,
          unbekannt.id,
          `${b.id}/unbekannt`,
          regeln,
          zustandKarte.get(b.artikel_id)?.lagerort_id === unbekannt.id,
        );
        if (weg !== null) offen.push(weg);
      }
    }
    const rechnen = async (ids: Iterable<string>, zusaetzlich: ReadonlyMap<string, Buchung[]>) => {
      const liste = [...ids];
      const log = await db.log.where("artikel_id").anyOf(liste).toArray();
      for (const id of liste) {
        const a = artikelKarte.get(id);
        if (a === undefined) continue;
        const eigene = [
          ...log.filter((b) => b.artikel_id === id),
          ...offen.filter((b) => b.artikel_id === id),
          ...(zusaetzlich.get(id) ?? []),
        ];
        zustandKarte.set(id, zustandBerechnen({ id, art: a.art }, eigene, regeln));
      }
    };

    await rechnen(wartend, new Map());
    // Wer archiviert ist und zuletzt eine abgeleitete Buchung bekam, ist mit
    // seiner Kiste verloren gegangen -- und kommt mit ihr zurueck (ADR 0016).
    const archivierte = zustaende.filter((z) => z.archiviert && z.letzte_buchung_id !== null);
    const letzte = await db.log.bulkGet(archivierte.map((z) => z.letzte_buchung_id ?? ""));
    const zuletztAbgeleitet = new Set(
      archivierte
        .filter((_, i) => (letzte[i]?.abgeleitet_von ?? null) !== null)
        .map((z) => z.artikel_id),
    );
    const mitgehend = inhaltVorwegnehmen(offen, lagerorte, zustandKarte, regeln, zuletztAbgeleitet);
    if (mitgehend.size > 0) await rechnen(mitgehend.keys(), mitgehend);
  }

  return {
    artikel: artikelKarte,
    zustaende: zustandKarte,
    lagerorte: new Map(lagerorte.map((l) => [l.id, l])),
    einsatzstellen: new Map(einsatzstellen.map((e) => [e.id, e])),
    benutzer: new Map(benutzer.map((b) => [b.id, b])),
    statusArten: arten,
    basis_adresse: basis,
    wartend,
  };
}

// --- Ein gemeinsamer Bestand fuer alle Komponenten ------------------------

let aktuell: Bestand | undefined;
const hoerer = new Set<() => void>();
let abo: { unsubscribe: () => void } | null = null;

/**
 * Eine Beobachtung fuer alle: Jeder Statuspunkt auf der Seite fragt nach den
 * Status, und jeder soll dieselbe Antwort bekommen, ohne dass der Bestand
 * zwanzigmal gelesen wird.
 */
function abonnieren(tun: () => void): () => void {
  hoerer.add(tun);
  abo ??= liveQuery(bestandLaden).subscribe({
    next: (bestand) => {
      aktuell = bestand;
      for (const h of hoerer) h();
    },
    error: (fehler: unknown) => {
      console.error("Der lokale Bestand ist nicht lesbar", fehler);
    },
  });
  return () => {
    hoerer.delete(tun);
    if (hoerer.size === 0) {
      abo?.unsubscribe();
      abo = null;
    }
  };
}

/** Der lokale Bestand; `undefined` nur im ersten Augenblick nach dem Start. */
export function useBestand(): Bestand | undefined {
  return useSyncExternalStore(abonnieren, () => aktuell);
}

/**
 * Die eingerichteten Status (ADR 0005). Ohne lokale Kopie -- vor dem ersten
 * Abgleich -- die Grundausstattung: schlimmstenfalls in der Farbe ungenau,
 * aber nie leer.
 */
export function useStatusArten(): readonly StatusArt[] {
  return useBestand()?.statusArten ?? STANDARD_STATUSARTEN;
}

export function useStatusRegeln(): StatusRegeln {
  const arten = useStatusArten();
  return useMemo(() => statusRegeln(arten), [arten]);
}

/**
 * Eine beliebige Abfrage an die lokale Datenbank, die sich selbst erneuert.
 *
 * Liefert `undefined`, bis die erste Antwort da ist -- und wieder, wenn sich
 * die Abhaengigkeiten aendern. Sonst zeigte die Artikelseite nach einem
 * Wechsel kurz den vorigen Artikel.
 */
export function useLokal<T>(
  abfrage: () => Promise<T>,
  abhaengig: readonly unknown[],
): T | undefined {
  const schluessel = JSON.stringify(abhaengig);
  const [stand, setStand] = useState<{ schluessel: string; wert: T } | undefined>(undefined);

  useEffect(() => {
    const beobachtung = liveQuery(abfrage).subscribe({
      next: (wert) => {
        setStand({ schluessel, wert });
      },
      error: (fehler: unknown) => {
        console.error("Lokale Abfrage gescheitert", fehler);
      },
    });
    return () => {
      beobachtung.unsubscribe();
    };
    // Die Abfrage selbst ist bei jedem Zeichnen eine neue Funktion; massgeblich
    // sind die Abhaengigkeiten, die der Aufrufer nennt.
  }, [schluessel]);

  return stand?.schluessel === schluessel ? stand.wert : undefined;
}

export interface SyncZahlen {
  wartend: number;
  abgelehnt: number;
  /** Ungelesene Hinweise im Sync-Protokoll (KONZEPT.md §15.4). */
  hinweise: number;
}

/** Die Zahlen fuer die Kopfzeile (KONZEPT.md §15.2). */
export function useSyncZahlen(): SyncZahlen | undefined {
  return useLokal(async () => {
    const [wartend, abgelehnt, hinweise] = await Promise.all([
      db.buchungen.where("zustand").equals("wartet").count(),
      db.buchungen.where("zustand").equals("abgelehnt").count(),
      db.hinweise.where("gelesen").equals(0).count(),
    ]);
    return { wartend, abgelehnt, hinweise };
  }, []);
}

// --- Abgeleitete Ansichten -------------------------------------------------

/**
 * Der Lagerortbaum mit Artikelzahl je Knoten -- so wie der Server ihn baut
 * (`core/db/src/stammdaten.ts`, `lagerortBaum`), nur aus der lokalen Kopie.
 */
export function lagerortBaum(bestand: Bestand): LagerortKnoten[] {
  const anzahl = new Map<string, number>();
  for (const z of bestand.zustaende.values()) {
    if (z.lagerort_id !== null) anzahl.set(z.lagerort_id, (anzahl.get(z.lagerort_id) ?? 0) + 1);
  }

  const knoten = new Map<string, LagerortKnoten>();
  const sortiert = [...bestand.lagerorte.values()].sort((a, b) =>
    a.kennung.localeCompare(b.kennung, "de"),
  );
  for (const l of sortiert) {
    knoten.set(l.id, { ...l, anzahl_artikel: anzahl.get(l.id) ?? 0, kinder: [] });
  }

  const wurzeln: LagerortKnoten[] = [];
  for (const k of knoten.values()) {
    const eltern = k.eltern_id === null ? undefined : knoten.get(k.eltern_id);
    if (eltern === undefined) wurzeln.push(k);
    else eltern.kinder.push(k);
  }
  return wurzeln;
}

export interface Verlauf {
  /** Was der Server hat, in der Reihenfolge, in der es gilt. */
  log: Buchung[];
  /** Was noch in der Warteschlange liegt. */
  wartend: Buchung[];
  /** Die ausloesenden Buchungen abgeleiteter Buchungen (Packstueck), soweit bekannt. */
  ausloeserinnen: ReadonlyMap<string, Buchung>;
}

/** Die Historie eines Artikels aus der lokalen Kopie. */
export async function verlaufLaden(artikel_id: string): Promise<Verlauf> {
  const [log, wartende] = await Promise.all([
    db.log.where("artikel_id").equals(artikel_id).toArray(),
    db.buchungen.where("zustand").equals("wartet").toArray(),
  ]);
  const verweise = [
    ...new Set(log.map((b) => b.abgeleitet_von).filter((v): v is string => v !== null)),
  ];
  const ausloeserinnen = (await db.log.bulkGet(verweise)).filter((b) => b !== undefined);
  return {
    log: sortiereBuchungen(log),
    wartend: sortiereBuchungen(
      wartende.filter((w) => w.buchung.artikel_id === artikel_id).map(alsBuchung),
    ),
    ausloeserinnen: new Map(ausloeserinnen.map((b) => [b.id, b])),
  };
}
